import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Plus, X, Upload, FileText, Trash2, FileSpreadsheet, Download, Loader2, Undo2 } from 'lucide-react';
import { Division26ListPage } from './shared/Division26ListPage';
import { Division26CreateModal } from './shared/Division26CreateModal';
import { Division26ReviewModal } from './shared/Division26ReviewModal';
import { equipmentListConfig, equipmentFormConfig, equipmentReviewConfig, EQUIPMENT_DATA_TYPES } from './equipmentConfig';
import { useSubmittalsManager } from '../shared/useSubmittalsManager';

export const EquipmentSection = ({
  projectId, csiItem, canEditCSIItems, canManageProject,
  onItemCount,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [equipTypes, setEquipTypes] = useState([]);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [mfrFilter, setMfrFilter] = useState('');

  // Import Excel
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  // Submittals manager (Division 7 pattern)
  const submittals = useSubmittalsManager();
  const fileInputRef = useRef(null);
  const [pendingDataType, setPendingDataType] = useState('');

  const csiItemId = csiItem?.id;

  useEffect(() => {
    if (onItemCount) onItemCount(items.length);
  }, [items.length, onItemCount]);

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}${equipmentListConfig.apiBase}/${projectId}${params}`);
      setItems(res.data);
    } catch {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/electrical-equipment-types`);
      setEquipTypes(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); fetchTypes(); }, [fetchItems, fetchTypes]);

  // Derive unique filter values
  const uniqueLocations = useMemo(() => [...new Set(items.map(i => i.location).filter(Boolean))].sort(), [items]);
  const uniqueMfrs = useMemo(() => [...new Set(items.map(i => i.manufacturer).filter(Boolean))].sort(), [items]);
  const uniqueTypes = useMemo(() => [...new Set(items.map(i => i.equipmentType).filter(Boolean))].sort(), [items]);

  // Filter + Search (search handled by Division26ListPage, filter here)
  const filteredItems = useMemo(() => {
    let result = items;
    if (typeFilter) result = result.filter(i => i.equipmentType === typeFilter);
    if (locationFilter) result = result.filter(i => i.location === locationFilter);
    if (mfrFilter) result = result.filter(i => i.manufacturer === mfrFilter);
    return result;
  }, [items, typeFilter, locationFilter, mfrFilter]);

  const hasFilters = typeFilter || locationFilter || mfrFilter;
  const clearFilters = () => { setTypeFilter(''); setLocationFilter(''); setMfrFilter(''); };

  // Single open selectbox behavior
  const [openDropdown, setOpenDropdown] = useState(null);
  const makeDropdownProps = (key) => ({
    open: openDropdown === key,
    onOpenChange: (open) => setOpenDropdown(prev => open ? key : (prev === key ? null : prev)),
    modal: false,
  });

  // ── Import / Sample Excel ──
  const handleSampleDownload = async () => {
    try {
      const res = await axios.get(`${API}${equipmentListConfig.apiBase}/${projectId}/sample-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'equipment_sample.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download sample'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.post(`${API}${equipmentListConfig.apiBase}/${projectId}/import-excel${params}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.imported} equipment imported`);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  // ── Submittals commit (adapted from Division 7 commitSubmittals) ──
  const commitEquipmentSubmittals = async (equipId) => {
    for (const pending of submittals.pendingUploads) {
      const fd = new FormData();
      fd.append('file', pending.file);
      fd.append('data_type', pending.docType || '');
      try {
        await axios.post(`${API}${equipmentListConfig.apiBase}/${projectId}/${equipId}/submittals`, fd);
      } catch { toast.error(`Failed to upload ${pending.fileName}`); }
    }
    for (const id of submittals.pendingDeletes) {
      try {
        await axios.delete(`${API}${equipmentListConfig.apiBase}/${projectId}/${equipId}/submittals/${id}`);
      } catch { toast.error('Failed to delete submittal'); }
    }
  };

  const handleSave = async (formData, existingItem) => {
    const payload = { ...formData, csiItemId };
    if (payload.typeId) {
      const foundType = equipTypes.find(t => t.id === payload.typeId);
      payload.equipmentType = foundType ? foundType.name : payload.typeId;
    }
    if (payload.ratingValue !== '' && payload.ratingValue != null) payload.ratingValue = parseFloat(payload.ratingValue);
    else payload.ratingValue = null;

    let equipId;
    if (existingItem) {
      await axios.put(`${API}${equipmentListConfig.apiBase}/${projectId}/${existingItem.id}`, payload);
      equipId = existingItem.id;
      toast.success('Equipment updated');
    } else {
      const res = await axios.post(`${API}${equipmentListConfig.apiBase}/${projectId}`, payload);
      equipId = res.data.id;
      toast.success('Equipment created');
    }

    if (submittals.isDirty) {
      await commitEquipmentSubmittals(equipId);
    }
    submittals.init([]);
    fetchItems();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}${equipmentListConfig.apiBase}/${projectId}/${id}`);
    setItems(prev => prev.filter(e => e.id !== id));
  };

  const handleBulkDelete = async (ids) => {
    const res = await axios.post(`${API}${equipmentListConfig.apiBase}/${projectId}/bulk-delete`, { ids });
    setItems(prev => prev.filter(e => !ids.includes(e.id)));
    return res.data.deleted || 0;
  };

  const handleCreateType = async (name) => {
    const res = await axios.post(`${API}/electrical-equipment-types`, { name });
    setEquipTypes(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    return res.data;
  };

  const handleEditClick = (item) => {
    const typeMatch = equipTypes.find(t => t.name === item.equipmentType);
    setEditingItem({ ...item, typeId: typeMatch ? typeMatch.id : '' });
    submittals.init(item.submittals || []);
    setShowModal(true);
  };

  const handleModalClose = (open) => {
    if (!open) {
      setShowModal(false);
      setEditingItem(null);
      submittals.rollback();
      setPendingDataType('');
    }
  };

  // ── Submittals section (Division 7 pattern) ──
  const submittalsSection = (
    <div className="mt-6 pt-4 border-t" data-testid="submittals-section">
      <div className="flex items-center justify-between mb-3 gap-2">
        <Label className="text-sm font-semibold text-slate-700">Submittals</Label>
        <div className="flex items-center gap-2">
          <Select value={pendingDataType} onValueChange={setPendingDataType} modal={false}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="submittal-data-type-select">
              <SelectValue placeholder="Data Type" />
            </SelectTrigger>
            <SelectContent>
              {EQUIPMENT_DATA_TYPES.map(dt => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="add-submittal-btn">
            <Plus className="h-3.5 w-3.5 mr-1" />Add File
          </Button>
        </div>
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) submittals.addUpload(file, pendingDataType);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      </div>

      {submittals.displayList.length === 0 ? (
        <p className="text-xs text-slate-400">No submittals added yet.</p>
      ) : (
        <div className="space-y-1.5">
          {submittals.displayList.map((item) => {
            const isDeleted = item._status === 'pendingDelete';
            return (
              <div key={item.id} className={`flex items-center gap-2 text-sm rounded px-3 py-2 ${isDeleted ? 'bg-red-50 opacity-60 line-through' : 'bg-slate-50'}`} data-testid={`submittal-row-${item.id}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="truncate flex-1">{item.fileName || item.name || 'file'}</span>

                <span className="text-xs text-slate-400 shrink-0">{((item.bytes || item.size || 0) / 1024).toFixed(1)} KB</span>

                <span className="text-xs bg-slate-200 text-slate-600 rounded px-1.5 py-0.5 w-20 text-center truncate shrink-0">{item.dataType || item.documentType || '-'}</span>

                {isDeleted ? (
                  <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-orange-500" onClick={() => submittals.undoDelete(item.id)} title="Undo" data-testid={`submittal-undo-${item.id}`}>
                    <Undo2 className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => submittals.removeItem(item.id)} data-testid={`submittal-remove-${item.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Filter Bar ──
  const filterBar = (
    <>
      <Select value={typeFilter} onValueChange={(val) => setTypeFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('type')}>
        <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-type">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Types</SelectItem>
          {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={locationFilter} onValueChange={(val) => setLocationFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('location')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-location">
          <SelectValue placeholder="All Locations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Locations</SelectItem>
          {uniqueLocations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={mfrFilter} onValueChange={(val) => setMfrFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('manufacturer')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-manufacturer">
          <SelectValue placeholder="All Manufacturers" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Manufacturers</SelectItem>
          {uniqueMfrs.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="clear-filters-btn">
          <X className="h-3.5 w-3.5 mr-1" />Clear
        </Button>
      )}
    </>
  );

  return (
    <>
      <Division26ListPage
        config={equipmentListConfig}
        items={items}
        loading={loading}
        projectId={projectId}
        csiItem={csiItem}
        canEditCSIItems={canEditCSIItems}
        canManageProject={canManageProject}
        onCreateClick={() => { setEditingItem(null); submittals.init([]); setShowModal(true); }}
        onEditClick={handleEditClick}
        onReviewClick={(item) => setReviewingItem(item)}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        filterBar={filterBar}
        filteredItems={filteredItems}
        showShare={false}
        extraToolbarButtons={
          <>
            <Button variant="outline" size="sm" onClick={handleSampleDownload} data-testid="elec-equip-sample-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />Sample Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} disabled={importing} data-testid="elec-equip-import-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import Excel
            </Button>
            <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </>
        }
      />

      <Division26CreateModal
        open={showModal}
        onOpenChange={handleModalClose}
        config={equipmentFormConfig}
        editItem={editingItem}
        onSave={handleSave}
        manufacturers={equipTypes}
        onCreateManufacturer={handleCreateType}
        extraSection={submittalsSection}
        projectId={projectId}
      />

      <Division26ReviewModal
        open={!!reviewingItem}
        onOpenChange={(open) => { if (!open) setReviewingItem(null); }}
        config={equipmentReviewConfig}
        item={reviewingItem}
      />
    </>
  );
};
